<template>
  <v-row justify="space-around">
    <v-col cols="12" md="8">
      <!-- Contact Section -->
      <h4 class="primary--text font-weight-medium">{{ $t('settings/profile:common.contact') }}</h4>
      <v-radio-group row v-model="data.type" :rules="[ruleRequired($t('utils:rules.required'))]">
        <v-radio :label="$t('common:customer')" value="CUSTOMER"/>
        <v-radio :label="$t('settings/profile:customer.settings.supplier')" value="SUPPLIER"/>
      </v-radio-group>

      <!-- Contact Name -->
      <v-text-field
          outlined
          clearable
          v-model="data.contactName"
          :label="$t('common:name')"
          :rules="[ruleRequired($t('utils:rules.required'))]"
          tabindex="1"
      />

      <!-- Contact Mail -->
      <v-text-field
          outlined
          clearable
          v-model="data.contactMail"
          :label="$t('common:email')"
          :rules="[ruleRequired($t('utils:rules.required'))]"
          tabindex="2"
      />

      <!-- Contact Phone -->
      <v-text-field
          outlined
          clearable
          v-model="data.contactPhone"
          :label="$t('settings/profile:common.phone')"
          tabindex="3"
      />

      <!-- General Section -->
      <h4 class="primary--text font-weight-medium">{{ $t('common:general') }}</h4>
      <!-- Name -->
      <v-text-field
          outlined
          v-model="data.name"
          :label="$t('common:name')"
          clearable
          :rules="[ruleRequired($t('utils:rules.required'))]"
          tabindex="4"
      />

      <!-- Address Section -->
      <h4 class="primary--text font-weight-medium">{{ $t('common:address') }}</h4>
      <!-- Street -->
      <v-text-field
          outlined
          clearable
          v-model="data.street"
          :label="$t('common:street')"
          tabindex="5"
      />
      <v-row>
        <v-col cols="12" sm="6">
          <!-- ZIP -->
          <v-text-field
              outlined
              clearable
              v-model="data.zip"
              :label="$t('common:zip')"
              tabindex="6"
              @input="getCityProvinceByZIP"
          />
        </v-col>
        <v-col cols="12" sm="6">
          <!-- City -->
          <v-text-field
              outlined
              clearable
              v-model="data.city"
              :label="$t('common:city')"
              tabindex="7"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <!-- Country -->
          <v-select
              outlined
              clearable
              v-model="data.country"
              :label="$t('common:country')"
              :menu-props="{ bottom: true, offsetY: true}"
              :items="countries"
              item-text="translatedName"
              item-value="id"
              @change="updateProvince"
              tabindex="8"
          />
        </v-col>
        <v-col>
          <!-- Province -->
          <v-select
              outlined
              v-model="data.province"
              :label="$t('common:province')"
              :items="provinces"
              item-text="translatedName"
              item-value="id"
              :menu-props="{ bottom: true, offsetY: true}"
              tabindex="9"
          />
        </v-col>
      </v-row>

      <h4 class="primary--text font-weight-medium">{{ $t('settings/profile:customer.settings.report-settings') }}</h4>
      <!-- Website -->
      <v-text-field
          outlined
          v-model="data.websiteUrl"
          label="Website"
          clearable
          tabindex="10"
      />

      <!-- Logo -->
      <h6>Logo</h6>
      <DragAndDropFile
          ref="dragAndDrop"
          @fileChange="uploadLogo($event)"
          notRequired
          custom-height="200px"
          preview-height="150px"
      />

      <!-- Footer content -->
      <v-textarea
          outlined
          clearable
          v-model="data.reportFooterContent"
          :label="$t('settings/profile:customer.settings.footer')"
          tabindex="11"
          class="mt-5"
      />


    </v-col>
  </v-row>
</template>

<script>
import DragAndDropFile from '@/modules/core/components/DragAndDropFile';
import CustomerService from '@/modules/core/services/customer-service';
import CountryService from '@/modules/core/services/country-service';
import {ruleRequired} from '@/modules/core/services/rules';
import GPSUtils from "@/modules/core/services/gps-utils";
import Customer from '@/modules/core/data/customer';
import Utils from '@/modules/core/services/utils';

export default {
  name: "GeneralTab",
  components: {
    DragAndDropFile
  },
  props: {
    customer: {
      type: Object,
      default: new Customer()
    }
  },
  watch: {
    data: {
      deep: true,
      handler() {
        this.$emit('updateCustomer', this.data);
      }
    }
  },
  data() {
    return {
      countries: [],
      provinces: [],
      data: {},
      isFetchingCustomer: true
    }
  },
  methods: {
    ruleRequired,
    uploadLogo(file) {
      if (!file) {
        return;
      }

      CustomerService.uploadLogo(this.data.id, file,
          (event) => {
            this.$refs.dragAndDrop.progress = Math.round((100 * event.loaded) / event.total);
          },
          (res) => {
            Utils.showSuccessSnackbar(this.$t('snackbar:upload-success', {name: this.$t('settings/profile:customer.settings.snackbar.logo')}))
            if (res.data) {

              let docuToParse = {
                fileType: 'image/*',
                src: process.env.VUE_APP_ROOT_HOST + res.data + "?" + Math.random()
              }
              this.$refs.dragAndDrop.setPreviewSrc(docuToParse)
              this.$refs.dragAndDrop.isUploading = false;
            }
          },
          () => {
            Utils.showFailureSnackbar(this.$t('snackbar:upload-failure', {name: this.$t('settings/profile:customer.settings.snackbar.logo')}))
          }
      )
    },

    /**
     * Reads and sets the building city and province
     * by the entered ZIP
     */
    getCityProvinceByZIP() {
      // check if there is a zip and country set for the building
      if (this.data.zip && this.data.country) {

        const countryName = GPSUtils.getCountryNameById(this.data.country, this.countries);

        if (GPSUtils.isValidZIP(countryName.toLowerCase(), this.data.zip)) {
          GPSUtils.readCityProvinceByZIP(
              this.data.zip,
              countryName,
              this.$store.state.translation.language,
              this.provinces,
              (res) => {
                this.data.city = res.city;
                this.data.province = res.province;
              },
              () => {
                Utils.showFailureSnackbar(this.$t('settings/profile:common.zip-to-city-fail'))
              }
          )
        }
      }
    },


    updateProvince() {
      if (!this.data.country) {
        return;
      }
      CountryService.readProvincesOfCountry(this.data.country,
          (resp) => {
            this.provinces = resp.data;
            this.data.province = this.provinces[0].id;
            Utils.translateAllProvinces(this.countries, this.provinces, this.data.country);
          },
          (error) => {
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:province_plural')}))
            this.$logger.error(error);
          }
      )
    },
    translateProvinces() {
      for (var i = 0; i < this.countries.length; i++) {
        if (this.countries[i].id == this.data.country) {
          for (var j = 0; j < this.provinces.length; j++) {
            this.provinces[j].translatedName = Utils.translateProvinces(this.countries[i].name, this.provinces[j].name)
          }
        }
      }
    }
  },
  mounted() {
    this.data = Object.assign({}, this.customer);

    if (this.data.logoUrl) {
      let docuToParse = {
        fileType: 'image/*',
        src: process.env.VUE_APP_ROOT_HOST + this.data.logoUrl
      }
      this.$refs.dragAndDrop.setPreviewSrc(docuToParse)
    }

    CountryService.readAllFromAPI(
        (res) => {
          this.countries = res.data;
          Utils.translateAllCountries(this.countries)
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('settings/profile:common.country-data')}))
          this.$logger.error(error);
        }
    )

    CountryService.readProvincesOfCountry(this.data.country,
        (resp) => {
          this.provinces = resp.data;
          Utils.translateAllCountries(this.countries)
          Utils.translateAllProvinces(this.countries, this.provinces, this.data.country);
          if (!this.data.province) {
            this.data.province = this.provinces[0].id;
          }
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:province_plural')}))
          this.$logger.error(error);
        }
    )
  }
}

</script>